import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined"
import {
    Box,
    Divider,
    Hidden,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
    useTheme,
} from "@mui/material"
import { Fragment, ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { LCTOnlyDatasets } from "../../constants/enums"
import {
    SINGLE_DEMAND_DEVICE_NAV_LINK,
    SINGLE_GEN_DEVICE_NAV_LINK,
} from "../../router/routes"
import HelpIcon from "../helper/helpIcon"
import LctAvatar from "../pills/lct/lctAvatar"
import StatusChip from "../pills/status/statusChip"
import { AppCardContent, AppCardHeader } from "../surfaces/appCard"
import AppPaper from "../surfaces/appPaper"

const TTR = "TTR"

interface LiveAlertDevices {
    raisedAt: string
    systemReference: string
    dataset: string
}

interface NonCompliantDevices {
    type: string
    systemReference: string
    status: string
    dataset: string
}

interface ListDevicesProps {
    cardTitle: string
    cardDescription: string
    cardTooltip: string
    data: LiveAlertDevices[] | NonCompliantDevices[]
}

const ListDevices = ({
    cardTitle,
    cardDescription,
    cardTooltip,
    data,
}: ListDevicesProps): ReactElement => {
    const navigate = useNavigate()
    const theme = useTheme()

    return (
        <AppPaper sx={{ width: "100%" }}>
            <Stack flexDirection="row" justifyContent="space-between">
                <AppCardHeader
                    title={cardTitle}
                    subheader={cardDescription}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                />
                <Box>
                    <HelpIcon tooltip={cardTooltip} />
                </Box>
            </Stack>
            {data.length === 0 && (
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    height="100%"
                    marginTop={8}
                    marginBottom={4}
                    spacing={3}
                >
                    <AssignmentTurnedInOutlinedIcon
                        sx={{ fontSize: 90 }}
                        color="success"
                    />
                    <Stack alignItems="center">
                        <Typography variant="h5" fontWeight={500}>
                            Nothing to report!
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            You currently have no devices matching this criteria
                        </Typography>
                    </Stack>
                </Stack>
            )}
            <AppCardContent>
                <List
                    sx={{
                        p: 0,
                        width: "100%",
                        backgroundColor: "background.paper",
                        overflowY: "scroll",
                        maxHeight: 600,
                    }}
                >
                    {data.map((item) => (
                        <Fragment key={item.systemReference}>
                            <ListItem sx={{ paddingLeft: 0.6 }}>
                                {"type" in item && (
                                    <ListItemAvatar>
                                        <LctAvatar
                                            lcts={[item.type]}
                                            sx={{
                                                backgroundColor:
                                                    theme.palette.text.primary,
                                            }}
                                        />
                                    </ListItemAvatar>
                                )}
                                <ListItemButton
                                    onClick={() =>
                                        navigate(
                                            item.dataset === TTR
                                                ? SINGLE_GEN_DEVICE_NAV_LINK(
                                                      item.systemReference
                                                  )
                                                : SINGLE_DEMAND_DEVICE_NAV_LINK(
                                                      item.dataset as LCTOnlyDatasets,
                                                      item.systemReference
                                                  )
                                        )
                                    }
                                    sx={{ paddingY: 0 }}
                                >
                                    <ListItemText
                                        primaryTypographyProps={{
                                            variant: "body1",
                                        }}
                                        primary={
                                            <strong>
                                                {item.systemReference}
                                            </strong>
                                        }
                                        secondary={item.dataset}
                                    />
                                    <Hidden smDown>
                                        {"type" in item && (
                                            <StatusChip status={item.status} />
                                        )}
                                    </Hidden>
                                </ListItemButton>
                            </ListItem>
                            <Divider variant="middle" component="li" />
                        </Fragment>
                    ))}
                </List>
            </AppCardContent>
        </AppPaper>
    )
}

export default ListDevices
